import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * True when the subtree renders any literal text, i.e. the control already has
 * a visible label. Injecting an `aria-label` on top of visible text would
 * override the name a speech-input user reads off the screen (WCAG 2.5.3
 * "Label in Name"), so those are left alone.
 */
function hasVisibleText(node: ReactNode): boolean {
  return Children.toArray(node).some((child) => {
    if (typeof child === "string") return child.trim().length > 0;
    if (typeof child === "number") return true;
    if (isValidElement(child)) {
      return hasVisibleText((child.props as { children?: ReactNode }).children);
    }
    return false;
  });
}

export default function CustomTooltip({
  text,
  children,
  side = "right",
  className,
}: {
  text: string;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  // BREEZEAI-1013: the tooltip content lives in a Radix popper that only exists
  // while the tooltip is open, so it never becomes the trigger's accessible
  // name — icon-only buttons wrapped here reported `aria-label: null` and failed
  // WCAG 2.1 SC 4.1.2 (Name, Role, Value). Name the child from the tooltip text
  // instead, but only when it has no label of its own and no visible text.
  const labelledChildren =
    isValidElement(children) &&
    !(children.props as { "aria-label"?: string })["aria-label"] &&
    !(children.props as { "aria-labelledby"?: string })["aria-labelledby"] &&
    !hasVisibleText((children.props as { children?: ReactNode }).children)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          "aria-label": text,
        })
      : children;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{labelledChildren}</span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className={`bg-surface-hover text-white px-2 py-1 text-xs rounded ${className}`}
        >
          <div>{text}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
